import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { LocationMarkerIcon } from '../icons';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapboxMap.css';

export interface MapMarker {
  id: string;
  longitude: number;
  latitude: number;
  label?: string;
  imageUrl?: string;
  route?: string;
}

interface MapboxMapProps {
  /** Initial center coordinates [longitude, latitude] */
  center?: [number, number];
  /** Initial zoom level */
  zoom?: number;
  /** Map style URL */
  style?: string;
  /** CSS class name */
  className?: string;
  /** Markers to display on the map */
  markers?: MapMarker[];
  /** ID of the currently focused marker (will fly to this marker) */
  focusedMarkerId?: string;
  /** Array of marker IDs to fit bounds to (for centering on multiple markers) */
  fitBoundsMarkerIds?: string[];
  /** Whether the map should be interactive (pan, zoom, etc). Defaults to true */
  interactive?: boolean;
}

const MapboxMap: React.FC<MapboxMapProps> = ({
  center = [-95.9928, 36.1540], // Default to Tulsa
  zoom = 12,
  style = 'mapbox://styles/mapbox/dark-v11',
  className = '',
  markers = [],
  focusedMarkerId,
  fitBoundsMarkerIds,
  interactive = true,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Store initial values in refs to avoid re-creating map on every render
  const initialCenterRef = useRef(center);
  const initialZoomRef = useRef(zoom);
  const initialStyleRef = useRef(style);

  // Initialize map only once on mount
  useEffect(() => {
    const token = process.env.REACT_APP_MAPBOX_TOKEN;

    if (!token) {
      setError('Mapbox token not found. Please set REACT_APP_MAPBOX_TOKEN in your .env file and restart the dev server.');
      console.error('REACT_APP_MAPBOX_TOKEN is not set');
      return;
    }

    if (!mapContainerRef.current) return;

    mapboxgl.accessToken = token;

    try {
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: initialStyleRef.current,
        center: initialCenterRef.current,
        zoom: initialZoomRef.current,
        interactive,
      });

      map.on('load', () => {
        setIsLoaded(true);
      });

      mapRef.current = map;

      return () => {
        // Clean up markers
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current.clear();

        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
    } catch (err) {
      setError('Failed to initialize map. Please check your Mapbox token.');
      console.error('Error initializing Mapbox:', err);
    }
  }, []);

  // Update markers when they change
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;

    const map = mapRef.current;
    const existingMarkerIds = new Set(markersRef.current.keys());
    const newMarkerIds = new Set(markers.map(m => m.id));

    // Remove markers that are no longer in the list
    existingMarkerIds.forEach(id => {
      if (!newMarkerIds.has(id)) {
        const marker = markersRef.current.get(id);
        if (marker) {
          marker.remove();
          markersRef.current.delete(id);
        }
      }
    });

    // Add or update markers
    markers.forEach(markerData => {
      if (markersRef.current.has(markerData.id)) {
        // Update existing marker position
        const marker = markersRef.current.get(markerData.id);
        if (marker) {
          marker.setLngLat([markerData.longitude, markerData.latitude]);
        }
      } else {
        // Create new marker
        const el = document.createElement('div');
        el.className = 'mapbox-custom-marker';

        if (markerData.imageUrl) {
          el.innerHTML = `
            <div class="marker-pin marker-pin--with-image">
              <img src="${markerData.imageUrl}" alt="${markerData.label || ''}" class="marker-pin-image" />
            </div>
          `;
        } else {
          el.innerHTML = `
            <div class="marker-pin">
              <div class="marker-pin-inner"></div>
            </div>
          `;
        }

        const marker = new mapboxgl.Marker(el)
          .setLngLat([markerData.longitude, markerData.latitude])
          .addTo(map);

        if (markerData.label) {
          const popupContent = markerData.route
            ? `<a href="${markerData.route}" class="marker-popup-link">${markerData.label}</a>`
            : markerData.label;

          marker.setPopup(
            new mapboxgl.Popup({ offset: 25, closeButton: false })
              .setHTML(`<div class="marker-popup-content">${popupContent}</div>`)
          );
        }

        markersRef.current.set(markerData.id, marker);
      }
    });
  }, [markers, isLoaded]);

  // Fly to focused marker when it changes
  useEffect(() => {
    if (!mapRef.current || !isLoaded || !focusedMarkerId) return;

    const focusedMarker = markers.find(m => m.id === focusedMarkerId);
    if (focusedMarker) {
      mapRef.current.flyTo({
        center: [focusedMarker.longitude, focusedMarker.latitude],
        zoom: 14,
        duration: 1800,
        curve: 1.2,
        offset: [0, -150], // Shift marker up to center between search bar and cards
        easing: (t) => {
          // Smooth ease-in-out curve
          return t < 0.5
            ? 4 * t * t * t
            : 1 - Math.pow(-2 * t + 2, 3) / 2;
        },
        essential: true,
      });

      // Highlight the focused marker
      markersRef.current.forEach((marker, id) => {
        const el = marker.getElement();
        if (id === focusedMarkerId) {
          el.classList.add('marker-focused');
        } else {
          el.classList.remove('marker-focused');
        }
      });
    }
  }, [focusedMarkerId, markers, isLoaded]);

  // Fit bounds to multiple markers when fitBoundsMarkerIds changes
  useEffect(() => {
    if (!mapRef.current || !isLoaded || !fitBoundsMarkerIds || fitBoundsMarkerIds.length === 0) return;

    const markersToFit = markers.filter(m => fitBoundsMarkerIds.includes(m.id));
    if (markersToFit.length === 0) return;

    // If only one marker, just fly to it
    if (markersToFit.length === 1) {
      mapRef.current.flyTo({
        center: [markersToFit[0].longitude, markersToFit[0].latitude],
        zoom: 14,
        duration: 1200,
        essential: true,
      });
      return;
    }

    // Calculate bounds for multiple markers
    const bounds = new mapboxgl.LngLatBounds();
    markersToFit.forEach(m => bounds.extend([m.longitude, m.latitude]));

    mapRef.current.fitBounds(bounds, {
      padding: { top: 100, bottom: 200, left: 50, right: 350 },
      maxZoom: 14,
      duration: 1200,
    });
  }, [fitBoundsMarkerIds, markers, isLoaded]);

  if (error) {
    return (
      <div className={`mapbox-error ${className}`}>
        <div className="error-content">
          <h3>Map Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {!isLoaded && (
        <div className={`mapbox-loading ${className}`}>
          <div className="mapbox-loading__icon">
            <LocationMarkerIcon style={{ width: '32px', height: '32px' }} color="rgba(171, 171, 171, 0.8)" />
          </div>
        </div>
      )}
      <div ref={mapContainerRef} className={`mapbox-map ${isLoaded ? 'mapbox-map-loaded' : ''} ${className}`} />
    </>
  );
};

export default MapboxMap;
